/**
 * Positionen eines Lieferscheins korrigieren — aufgeklappt unter seiner Zeile.
 *
 * Benjamin, 02.10.2026: „da wurde ein falsches Gewicht erfasst — Menge und
 * Material müssen korrigierbar sein." Je Position: Material als Knopf
 * (Katalogsuche, auch neu anlegen), Menge und Einheit als Feld. Gespeichert
 * wird beim Verlassen des Feldes; was die KI nur vorgeschlagen hat, lässt sich
 * mit einem Tipp bestätigen. Fehlt eine Zeile oder ist eine doppelt gelesen:
 * „＋ Position" bzw. × — so lässt sich die Erfassung ganz nachkorrigieren.
 * Einheiten schreibt der Server einheitlich (t, m³, Stk … — einheiten.py).
 */
import { useState } from "react";
import type { ReactElement } from "react";

import { fehlerText } from "@vimaana/ui";

import {
  useLieferscheinPositionAendern, useLieferscheinPositionen, useLieferscheinPositionNeuWeg, type LieferscheinPosition,
} from "../../hooks/useLieferschein";
import MaterialWahl from "../lieferanten/MaterialWahl";
import "../../styles/lieferanten.css";

const SICHER = new Set(["gedaechtnis", "katalog", "mensch"]);
/** Vorschläge fürs Einheitenfeld — dieselben Schreibweisen wie einheiten.py. */
const EINHEITEN = ["t", "kg", "m³", "m²", "m", "lfm", "l", "Stk", "Sack", "Pck", "Palette", "Rolle"];

const zahlText = (n: number) => (n ? n.toLocaleString("de-DE", { maximumFractionDigits: 3 }) : "");

/** „17,4" / „17.4" / „1.200,5" → Zahl; leer oder Unsinn → null. */
function zahl(t: string): number | null {
  const s = t.trim().replace(/\s/g, "");
  if (!s) return null;
  const n = Number(s.includes(",") ? s.replace(/\./g, "").replace(",", ".") : s);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

export default function LieferscheinPositionen({ belegId, projektId }: { belegId: string; projektId: string }): ReactElement {
  const q = useLieferscheinPositionen(belegId, true);
  const aendern = useLieferscheinPositionAendern(belegId, projektId);
  const { neu, weg } = useLieferscheinPositionNeuWeg(belegId, projektId);
  const [dazu, setDazu] = useState(false);
  if (q.isPending) return <p className="lf-leer">Lade Positionen …</p>;
  if (q.isError) return <p className="lf-fehler">{fehlerText(q.error)}</p>;
  const positionen = q.data ?? [];
  const fehler = aendern.error || neu.error || weg.error;
  const listeId = `vg-einheiten-${belegId}`;
  return (
    <div className="vg-ls-pos-liste">
      {fehler && <p className="lf-fehler">{fehlerText(fehler)}</p>}
      <datalist id={listeId}>{EINHEITEN.map((e) => <option key={e} value={e} />)}</datalist>
      {positionen.length === 0 && <p className="lf-leer">Auf diesem Lieferschein wurden keine Positionen gelesen.</p>}
      <ul className="lf-liste">
        {positionen.map((p) => (
          // Der Schlüssel trägt Menge und Einheit: kommt vom Server ein anderer
          // Wert zurück (z. B. die Einheit vom Material), beginnt die Zeile neu.
          <Position key={`${p.id}|${p.menge}|${p.einheit}`} p={p} einheitenListe={listeId}
            speichern={(patch) => aendern.mutate({ id: p.id, ...patch })}
            entfernen={() => weg.mutate(p.id)} />
        ))}
      </ul>
      {dazu ? (
        <div className="vg-ls-dazu">
          <MaterialWahl onAbbruch={() => setDazu(false)} platzhalter="Material der fehlenden Position suchen …"
            onWahl={(m) => neu.mutate({ katalog_id: m.id, einheit: m.einheit }, { onSuccess: () => setDazu(false) })} />
        </div>
      ) : (
        <button type="button" className="lf-knopf vg-ls-dazu-knopf" disabled={neu.isPending} onClick={() => setDazu(true)}>
          ＋ Position
        </button>
      )}
    </div>
  );
}

function Position({ p, speichern, entfernen, einheitenListe }: {
  p: LieferscheinPosition;
  speichern: (patch: { katalog_id?: string; menge?: number; einheit?: string }) => void;
  entfernen: () => void;
  einheitenListe: string;
}): ReactElement {
  const [loeschen, setLoeschen] = useState(false);
  const [menge, setMenge] = useState(zahlText(p.menge));
  const [einheit, setEinheit] = useState(p.einheit);
  const [waehlen, setWaehlen] = useState(false);

  const sicher = SICHER.has(p.zuordnung) && !!p.katalog_id;
  const vorschlag = p.zuordnung === "vorschlag" && !!p.katalog_id;
  const ignoriert = p.zuordnung === "ignoriert";
  const materialText = sicher || vorschlag ? p.katalog_name : ignoriert ? "keine Ware" : "Material wählen";
  const mengeFehlerhaft = zahl(menge) === null && menge.trim() !== "";

  return (
    <li className={"lf-zeile vg-ls-pzeile" + (ignoriert ? " ist-alt" : "")}>
      <div className="lf-zeile-haupt">
        <button type="button" className={"vg-ls-material" + (sicher ? "" : " ist-offen")} aria-expanded={waehlen}
          onClick={() => setWaehlen((w) => !w)} title="Material ändern">
          {materialText}{vorschlag && <em> · Vorschlag</em>}
        </button>
        {(p.freitext_name && p.freitext_name !== p.katalog_name) && (
          <span className="lf-zeile-info">auf dem Lieferschein: {p.freitext_name}</span>
        )}
      </div>
      <div className="vg-ls-menge-felder">
        <input className={"lf-feld vg-ls-zahl" + (mengeFehlerhaft ? " ist-fehler" : "")} inputMode="decimal"
          aria-label={`Menge ${p.freitext_name}`} value={menge} onChange={(e) => setMenge(e.target.value)}
          onBlur={() => { const n = zahl(menge); if (n !== null && n !== p.menge) speichern({ menge: n }); }} />
        <input className="lf-feld vg-ls-einheit" aria-label={`Einheit ${p.freitext_name}`} value={einheit} list={einheitenListe}
          onChange={(e) => setEinheit(e.target.value)}
          onBlur={() => { if (einheit.trim() !== p.einheit) speichern({ einheit: einheit.trim() }); }} />
        {vorschlag && (
          <button type="button" className="lf-knopf is-primaer" onClick={() => speichern({ katalog_id: p.katalog_id })}
            title={`„${p.katalog_name}“ ist richtig`}>✓ Stimmt</button>
        )}
        <button type="button" className="lf-knopf vg-ls-weg" aria-label={`Position ${p.freitext_name} entfernen`}
          title="Position entfernen" onClick={() => setLoeschen((l) => !l)}>×</button>
      </div>
      {loeschen && (
        <div className="lf-knoepfe lf-zeile-unter">
          <span className="lf-hinweis">„{p.katalog_name || p.freitext_name}“ vom Lieferschein nehmen?</span>
          <button type="button" className="lf-knopf" onClick={() => setLoeschen(false)}>Nein</button>
          <button type="button" className="lf-knopf is-gefahr" onClick={entfernen}>Entfernen</button>
        </div>
      )}
      {waehlen && (
        <div className="lf-zeile-unter">
          <MaterialWahl start={p.freitext_name} onAbbruch={() => setWaehlen(false)}
            onWahl={(m) => { speichern({ katalog_id: m.id }); setWaehlen(false); }} />
        </div>
      )}
    </li>
  );
}
